import { Base3dView, PromiseCompletionSource } from "scene-manager";
import { DirectionalLight, DoubleSide, Intersection, Mesh, MeshStandardMaterial, Object3D, Object3DEventMap, OrthographicCamera, PerspectiveCamera, PlaneGeometry, Raycaster, Sprite, Vector2, Vector3 } from "three";
import { GameResourcesPackage } from "../resources/GameResourcesPackage";
import { RenderCamera } from "../../../three/RenderCamera";
import { Level } from "../components/3d/Level";
import { GameViewModel } from "../models/GameViewModel";
import { GameController } from "../controllers/GameController";
import { nameof } from "bindable-data";
import { isMobile, Renderer, Ticker } from "pixi.js";
import { Turret } from "../components/3d/turret/Turret";
import { Portal } from "../components/3d/gamefield/portal/Portal";
import { BoosterType } from "../../../common/models/data/BoosterType";
import { IProjectileContainer } from "../components/3d/gamefield/IProjectileContainer";
import { BatchedParticleRenderer, BatchedRenderer } from "three.quarks";
import { AudioController, Music } from "../components/AudioController";
import { Spawner } from "../components/3d/spawner/Spawner";
import { GameState } from "../models/data/GameState";
import { ISpawnable } from "../components/3d/spawner/ISpawnable";
import { PlayerModel } from "../../../common/models/PlayerModel";
import { BaseMole } from "../components/3d/gamefield/mole/BaseMole";
import { Mine } from "../components/3d/gamefield/mines/Mine";
import { PortalMole } from "../components/3d/gamefield/mole/PortalMole";
import { InterfaceCheckUtils } from "../../../common/utils/InterfaceCheckUtils";
import { GameModel } from "../models/GameModel";
import { Character } from "../components/3d/character/Character";
import { LevelModel } from "../../../common/models/LevelModel";
import { MoleHole } from "../components/3d/gamefield/mole/MoleHole";
import { EnergyBooster } from "../components/3d/gamefield/EnergyBooster";
import { FontResourcesPackage } from "../../../common/resources/FontResourcesPackage";
import { LocalizationModel } from "../../../common/models/LocalizationModel";
import { LightningParticle } from "../components/3d/gamefield/particles/LightningParticle";
import { Box } from "../components/3d/gamefield/box/Box";
import { SaveModel } from "../../../common/models/SaveModel";
import { Vegetable } from "../components/3d/gamefield/Vegetable";
import { Projectile } from "../components/3d/projectiles/abstract/Projectile";
import { Physics } from "../../../three/physics/Physics";
import { BoostersConfiguration } from "../../../common/models/BoostersConfiguration";
import { EnergyBoosterData } from "../../../common/models/data/EnergyBoosterData";
import { FreezeBoosterData } from "../../../common/models/data/FreezeBoosterData";
import { MineBoosterData } from "../../../common/models/data/MineBoosterData";
import { TurretBoosterData } from "../../../common/models/data/TurretBoosterData";
import { Tractor } from "../components/3d/gamefield/tractor/Tractor";
import { MoleProjectile } from "../components/3d/projectiles/MoleProjectile";
import { GameSceneOptions } from "../GameSceneOptions";

export class GameView3d extends Base3dView<void> implements IProjectileContainer {
    private readonly canvas: HTMLCanvasElement;
    private readonly raycaster: Raycaster;
    private readonly particlesBatchRenderer: BatchedRenderer;
    private readonly mouse: Vector2;
    private character: Character;
    private spawners: Array<Spawner>;
    private spawnedItems: Set<ISpawnable>;
    private level: Level;
    private turret: Turret;
    private energy: EnergyBooster;
    private energyBoosterCast: boolean = false;

    public get Character(): Character {
        return this.character;
    }

    public get Spawners(): ReadonlyArray<Spawner> {
        return this.spawners;
    }

    public get SpawnedItems(): ReadonlySet<ISpawnable> {
        return this.spawnedItems;
    }

    public get spawnedMoles(): Array<BaseMole> {
        return Array.from(this.spawnedItems)
            .map(item => {
                if (item instanceof BaseMole) {
                    return item;
                } else if (item instanceof MoleHole && item.mole instanceof BaseMole) {
                    return item.mole;
                }
                return null;
            })
            .filter(mole => mole != null);
    }

    public get portalMoles(): Array<PortalMole> {
        return Array.from(this.spawnedItems).filter(item => item instanceof PortalMole) as PortalMole[];
    }

    public get spawnedEnemies(): Array<ISpawnable> {
        return Array.from(this.spawnedItems).filter(item => {
            if (item instanceof Portal || item instanceof MoleHole || item instanceof BaseMole) {
                return true;
            }
            return false;
        })
    }

    public get canPlaceTurret(): boolean {
        return this.turret == null;
    }

    public get canPlaceMines(): boolean {
        return this.spawners.findIndex(spawner => !spawner.mined && !spawner.occupiedWithBox) !== -1 || this.portalMoles.findIndex(mole => !mole.mined) !== -1;
    }

    public get canApplyEnergy(): boolean {
        return this.energyBoosterCast == false && this.spawnedMoles.filter(mole => mole.alive).length > 0;
    }

    public get canApplyFreeze(): boolean {
        return this.spawnedMoles.filter(mole => mole.alive && !mole.freezed).length > 0;
    }

    public constructor(
        private readonly gameResources: GameResourcesPackage,
        private readonly fontResources: FontResourcesPackage,
        private readonly localization: LocalizationModel,
        private readonly gameModel: GameModel,
        private readonly saveModel: SaveModel,
        private readonly playerModel: PlayerModel,
        private readonly boostersConfiguration: BoostersConfiguration,
        private readonly levelModel: LevelModel,
        private readonly gameViewModel: GameViewModel,
        private readonly gameController: GameController,
        private readonly audioController: AudioController,
        private readonly renderCamera: RenderCamera,
        private readonly renderer: Renderer,
        public readonly initialHousesRotation: number,
        operationControls: PromiseCompletionSource<void>
    ) {
        super(operationControls);
        this.canvas = document.getElementsByTagName("canvas")[0] as HTMLCanvasElement;
        this.raycaster = new Raycaster();
        this.particlesBatchRenderer = new BatchedParticleRenderer();
        this.add(this.particlesBatchRenderer);
        this.mouse = new Vector2();
        this.spawners = [];
        this.spawnedItems = new Set();
        this.build();
        this.setupCamera();
        this.setupLightsAndShadows();
        this.onViewPointerUp = this.onViewPointerUp.bind(this);
        this.canvas.addEventListener("pointerup", this.onViewPointerUp);
        this.gameModel.propertyChanged.add(this.onGameModelPropertyChange, this);
        this.gameModel.restoreVegetables.add(this.onRestoreVegetables, this);
        this.gameModel.forceMoleSpawn.add(this.onForceMoleSpawn, this);
        this.gameViewModel.initialize(this);
        this.playerModel.applyBooster.add(this.onApplyBooster, this);
        this.character.propertyChanged.add(this.onCharacterChanged, this);
        Ticker.shared.add(this.onTick, this);
    }

    public override destroy(): void {
        this.gameModel.restoreVegetables.remove(this.onRestoreVegetables, this);
        for (const spawner of this.spawners) {
            spawner.onSpawned.remove(this.onItemSpawned, this);
            spawner.onDestroyed.remove(this.onItemDespawned, this);
            spawner.destroy();
        }
        for (const item of this.spawnedItems) {
            item.destroy();
        }
        this.canvas.removeEventListener("pointerup", this.onViewPointerUp);
        this.gameModel.propertyChanged.remove(this.onGameModelPropertyChange, this);
        this.playerModel.applyBooster.remove(this.onApplyBooster, this);
        this.renderCamera.current = null;
        this.character.destroy();
        while (this.children.length > 0) {
            const child = this.children[0];
            this.remove(child);
            if (child instanceof Projectile) {
                child.destroy();
            }
        }

        Ticker.shared.remove(this.onTick, this);
        Physics.removeAll();
        super.destroy();
    }

    public async start(): Promise<void> {
        this.audioController.playMusic(Music.LevelBackground);
    }

    private build(): void {
        this.character = new Character(this.gameResources, this.gameViewModel, this.levelModel, this.saveModel, this.playerModel, this.audioController, this, this.particlesBatchRenderer);
        this.add(this.character);
        this.level = new Level(this.gameResources, this.initialHousesRotation);
        this.add(this.level);

        const levelData = this.levelModel.levelConfiguration;
        for (const spawnerConfiguration of levelData.spawnersConfiguration) {
            const spawner = new Spawner(
                spawnerConfiguration.spawnerConfiguration,
                this.gameResources,
                this.fontResources,
                this.particlesBatchRenderer,
                this.localization,
                this.gameViewModel,
                this,
                this.gameController,
                this.character,
                this.audioController,
                spawnerConfiguration.index
            );
            this.add(spawner);
            this.spawners.push(spawner);
            spawner.position.copy(this.gameViewModel.indexToGameCoords(spawner.fieldIndex));
            spawner.onSpawned.add(this.onItemSpawned, this);
            spawner.onDestroyed.add(this.onItemDespawned, this);
        }
    }

    private setupCamera(): void {
        const sceneCamera = new PerspectiveCamera(50, window.innerWidth / window.innerHeight);
        const cameraXAngle = 18 * Math.PI / 180;
        const cameraZ = 9;
        const cameraY = 5;
        sceneCamera.rotation.set(cameraXAngle, Math.PI, 0.0);
        sceneCamera.position.set(0, cameraY, -cameraZ);
        this.renderCamera.current = sceneCamera;
    }

    private setupLightsAndShadows(): void {
        // Direct. Used only for shadows for now as we have all materials set to unlit.
        const mainLight = new DirectionalLight(0xffffff, 2);
        mainLight.position.set(-8, 25, 20);
        mainLight.rotation.set(0, 0, 0);
        this.add(mainLight);
        //Set up shadow properties for the light
        mainLight.castShadow = true;
        mainLight.shadow.mapSize.width = 512;
        mainLight.shadow.mapSize.height = 512;
        mainLight.shadow.camera.near = 0.1;
        mainLight.shadow.camera.far = 110;
        mainLight.shadow.radius = 5;
    }

    private onTick(_dt: number): void {
        this.particlesBatchRenderer.update(Ticker.shared.deltaMS * 0.001);
    }

    private async onViewPointerUp(event: PointerEvent): Promise<void> {
        // Skip clicks when we are paused, win or lose
        if (this.gameModel.state.value !== GameState.Playing || !Ticker.shared.started) {
            return;
        }
        // Hack to not process pixi ui pointer up events
        const pointerData = this.renderer.events.rootBoundary["mappingState"].trackingData[event.pointerId];
        if (pointerData != null && pointerData.pressTargetsByButton[0] != null && pointerData.pressTargetsByButton[0].length > 0) {
            return;
        }
        const intersects = this.countClickIntersections(event.clientX, event.clientY);

        if (intersects.length === 0) {
            return;
        }
        const intersectPoint = intersects[0].point.clone();
        if (intersectPoint.z < 2) {
            return;
        }
        await this.throwProjectile(intersectPoint);
    }

    private countClickIntersections(x: number, y: number): Intersection<Object3D<Object3DEventMap>>[] {
        const rect = this.canvas.getBoundingClientRect();
        this.mouse.x = ((x - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((y - rect.top) / rect.height) * 2 + 1;

        // Raycast
        this.raycaster.setFromCamera(this.mouse, this.renderCamera.current);
        const intersects = this.raycaster.intersectObject(this, true).filter(item => !(item.object?.parent instanceof Vegetable) && !(item.object instanceof Sprite));
        // We intersect nothing of possible targets
        if (intersects.length == 0) {
            return;
        }
        // Check for clickable item like portal 
        for (const intersection of intersects) {
            const object = intersection.object;
            if (object.parent.parent instanceof Portal) {
                // We tap Portal
                object.parent.parent.hit();
                return [];
            }
            if (object.parent instanceof Box) {
                object.parent.takeDamage(1);
                return [];
            }
        }
        return intersects;
    }

    /**
     * Get the world position of the left/right edge of the screen
     * at a given Z depth (in world space).
     */
    private getScreenEdgesAtZ(camera: PerspectiveCamera | OrthographicCamera, z: number, offset: number = 3): { left: Vector3; right: Vector3 } {
        const cameraPos = camera.position.clone();

        // NDC positions for left/right edge
        const leftNDC = new Vector3(-1, 0, 0);
        const rightNDC = new Vector3(1, 0, 0);

        const ndcToWorld = (ndc: Vector3) => {
            const worldPoint = ndc.clone().unproject(camera);
            const dir = worldPoint.sub(cameraPos).normalize();
            const t = (z - cameraPos.z) / dir.z; // intersection scale
            return cameraPos.clone().add(dir.multiplyScalar(t));
        };

        const left = ndcToWorld(leftNDC);
        const right = ndcToWorld(rightNDC);

        // Apply offset along X direction (screen horizontal axis in world space)
        const xAxis = right.clone().sub(left).normalize();
        left.addScaledVector(xAxis, -offset).setY(0);
        right.addScaledVector(xAxis, offset).setY(0);

        return { left, right };
    }

    private async throwProjectile(originalWorldPosition: Vector3): Promise<void> {
        const targetWorldPosition = originalWorldPosition.clone();
        this.character.throw(targetWorldPosition);
    }

    private onForceMoleSpawn(): void {
        const freeSpawners = this.spawners.filter(spawner => !spawner.occupied).shuffle();
        freeSpawners[0].forceSpawn();
    }

    private onRestoreVegetables(count: number): void {
        for (const spawner of this.spawners) {
            if (!spawner.withVegetable) {
                continue;
            }
            spawner.restoreVegetable();
            if (--count === 0) {
                break;
            }
        }
    }

    private onApplyBooster(booster: BoosterType, level: number): void {
        switch (booster) {
            case BoosterType.Mine: {
                this.dropMines(level);
                break;
            }
            case BoosterType.Turret: {
                this.createTurret(level);
                break;
            }
            case BoosterType.Freeze: {
                this.freezeMoles(level);
                break;
            }
            case BoosterType.Energy: {
                this.lightningStrike(level);
                break;
            }
        }
    }

    public dropMines(level: number): void {
        const mineConfig = this.boostersConfiguration.getConfiguration(BoosterType.Mine, level) as MineBoosterData;
        let mined: number = 0;
        const availablePlaces = [...this.spawners.filter(spawner => !spawner.mined && !spawner.withBox), ...this.portalMoles.filter(mole => mole.isFlyEnded && !mole.mined)];

        while (mined < mineConfig.count && availablePlaces.length > 0) {
            const place = availablePlaces.shift();
            if (InterfaceCheckUtils.isMinable(place)) {
                place.mine = new Mine(this.gameResources, this.particlesBatchRenderer);
            } else {
                console.warn("GameView3d try drop mine to not IMinable place!");
            }
            mined++;
        }
    }

    public getScreenCoordinatesByFieldIndex(index: number) {
        const rect = this.canvas.getBoundingClientRect();
        const pos = this.gameViewModel.indexToGameCoords(index);
        pos.setY(2.5);

        // Project to NDC (Normalized Device Coordinates)
        pos.project(this.renderCamera.current);

        // Convert to pixel coordinates relative to canvas
        const x = (pos.x * 0.5 + 0.5) * rect.width + rect.left;
        const y = (-pos.y * 0.5 + 0.5) * rect.height + rect.top;

        return { x, y };
    }

    private createTurret(level: number): void {
        const turretConfig = this.boostersConfiguration.getConfiguration(BoosterType.Turret, level) as TurretBoosterData;
        this.turret = new Turret(this.gameResources, this.audioController, this.gameViewModel, turretConfig, this);
        this.turret.position.set(isMobile.phone ? 1.3 : 3, 0, 0);
        this.add(this.turret);
        this.turret.timeOut.once(this.removeTurret, this);
    }

    private removeTurret(): void {
        this.turret?.destroy();
        this.turret = null;
    }

    private freezeMoles(level: number): void {
        const config = this.boostersConfiguration.getConfiguration(BoosterType.Freeze, level) as FreezeBoosterData;
        const aliveMoles = this.spawnedMoles.filter(item => item.alive);
        for (const mole of aliveMoles) {
            mole.freeze(config.freezeTime);
        }
    }

    private async lightningStrike(level: number): Promise<void> {
        this.energyBoosterCast = true;
        const config = this.boostersConfiguration.getConfiguration(BoosterType.Energy, level) as EnergyBoosterData;
        const targetMole = this.spawnedMoles.find(item => item.alive);

        if (targetMole == null) {
            console.warn("GameView3d.lightningStrike can't find target mole!");
            this.energyBoosterCast = false;
            return;
        }

        const lightning = new LightningParticle(this.gameResources, this.particlesBatchRenderer);
        const targetPosition = this.gameViewModel.indexToGameCoords(targetMole.fieldIndex);
        targetPosition.z -= 0.5;
        lightning.position.copy(targetPosition);
        this.add(lightning);
        await lightning.play();

        this.energy = new EnergyBooster(this.gameResources, this.audioController, this.particlesBatchRenderer, this.gameViewModel, config, targetPosition);
        this.energy.onDestroy.add(this.removeEnergyBooster, this);
        this.add(this.energy);
        this.energy.updateMatrixWorld(true);
    }

    private removeEnergyBooster(): void {
        this.energy.onDestroy.remove(this.removeEnergyBooster, this);
        this.energy = null;
        this.energyBoosterCast = false;
    }

    private async onGameModelPropertyChange(model: GameModel, key: string): Promise<void> {
        if (key === nameof(model, "state")) {
            switch (model.state.value) {
                case GameState.Playing: {
                    this.resumeSpawners();
                    break;
                }
                case GameState.Victory: {
                    this.pauseSpawners();
                    this.turret?.cancel();
                    this.clearMines();
                    const tractorAnimation = this.playTractorAnimation();
                    const characterAnimation = this.character.playVictoryAnimation();
                    await Promise.all([tractorAnimation, characterAnimation]);
                    const nextHousesRotation = this.initialHousesRotation - (120 / 360)
                    const nextGameOptions: GameSceneOptions = {
                        initialHousesRotation: nextHousesRotation,
                    };
                    this.level.rotateHouses(this.initialHousesRotation, nextHousesRotation);
                    this.gameController.showVictoryPopup(this.operationControls, nextGameOptions);
                    this.removeTurret();
                    break;
                }
                case GameState.Lose: {
                    this.pauseSpawners();
                    this.turret?.cancel();
                    this.clearEnemies();
                    this.clearMolesProjectiles();

                    if (this.gameModel.isVegetablesGone) {
                        await this.character.playLoseAnimation();
                    } else {
                        await this.character.deathAnimationEnd();
                    }
                    const restartGameOptions: GameSceneOptions = {
                        initialHousesRotation: this.initialHousesRotation
                    };
                    this.gameController.showLosePopup(this.operationControls, restartGameOptions);
                    this.removeTurret();
                    break;
                }
            }
        }
        if (key === nameof(model, "time")) {
            if (model.time.value === 0) {
                this.pauseSpawners();
            }
        }
    }

    // special method to count moles from portal
    public addSpawnableItem(item: ISpawnable): void {
        this.onItemSpawned(item);
        this.add(item);
        item.onCanBeDestroyed.once(() => {
            item.destroy();
            this.onItemDespawned(item);
        });
    }

    private onItemSpawned(item: ISpawnable): void {
        if (item instanceof Portal || item instanceof BaseMole || item instanceof MoleHole) {
            this.gameController.handleEnemySpawn();
        }
        this.spawnedItems.add(item);
    }

    private onItemDespawned(item: ISpawnable): void {
        if (item instanceof Portal || item instanceof BaseMole || item instanceof MoleHole) {
            this.gameController.handleEnemyDespawn();
        }
        this.spawnedItems.delete(item);
    }

    private onCharacterChanged(character: Character, key: string): void {
        if (key === nameof(character, "health")) {
            character.health.value === 0 && this.gameController.handleCharacterDie();
        }
    }

    private resumeSpawners(): void {
        this.spawners.forEach((spawner, index) => spawner.start(index * 500));
    }

    private pauseSpawners(): void {
        this.spawners.forEach(spawner => spawner.stop());
    }

    private async playTractorAnimation(): Promise<void> {
        const { left, right } = this.getScreenEdgesAtZ(this.renderCamera.current, isMobile.phone ? 20 : 15, 6);
        const vegetables = this.spawners.map(spawner => spawner.tractorVegetable).filter(v => v != null).map(item => {
            const worldPosition = item.vegetableObjectForTractor.getWorldPosition(new Vector3());
            this.add(item.vegetableObjectForTractor);
            item.vegetableObjectForTractor.position.copy(worldPosition);
            return item.vegetableObjectForTractor;
        });
        const tractor = new Tractor(this.gameResources, this.particlesBatchRenderer, vegetables, right, left);
        this.add(tractor);
        tractor.updateMatrixWorld(true);
        await tractor.animate();
        tractor.destroy();
    }

    private clearEnemies(): void {
        const itemsToDestroy = this.spawnedEnemies;
        for (let i = 0; i < itemsToDestroy.length; i++) {
            const item = itemsToDestroy[i];
            if (item instanceof MoleHole) {
                if (item.mole == null) {
                    item.cancellation.cancel();
                } else {
                    item.mole.onCanBeDestroyed.emit(item);
                }
            } else if (item instanceof Portal) {
                item.cancellation.cancel();
            } else {
                item.onCanBeDestroyed.emit(item);
            }
        }
    }

    private clearMolesProjectiles(): void {
        const projectiles = this.children.filter(item => item instanceof MoleProjectile);
        for (let i = 0; i < projectiles.length; i++) {
            const item = projectiles[i];
            if (item instanceof MoleProjectile) {
                item.destroy();
            }
        }
    }

    private clearMines(): void {
        this.spawners.forEach(spawner => spawner.clearMine());
    }

    private debugSpawnersPositions(): void {
        const totalSize = GameViewModel.GAME_FIELD_WIDTH * GameViewModel.GAME_FIELD_HEIGHT;
        const indices = Array.from({ length: totalSize }, (_, i) => i);
        const planeGeometry = new PlaneGeometry(1, 1);
        const planeMaterial = new MeshStandardMaterial({ color: 0x00ff00, side: DoubleSide });

        indices.forEach((index) => {
            const plane = new Mesh(planeGeometry, planeMaterial);
            plane.rotation.x = -Math.PI / 2;
            const coord = this.gameViewModel.indexToGameCoords(index);
            plane.position.copy(coord);
            plane.position.y = 0.1;
            this.add(plane);
        });
    }
}
